import React from 'react';
import { Compass } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';

const MissionSummary = () => {
  const missions = [
    {
      name: 'Calypso Ridge Survey',
      status: 'Active',
      statusVariant: 'good',
      eta: '2025-11-21',
      progress: 42,
      kpis: [
        { k: 'Area mapped', v: '312 km²' },
        { k: 'Profiles', v: '78' },
        { k: 'Specimens', v: '12' },
      ],
    },
    {
      name: 'Seamount Transect 12',
      status: 'Standby',
      statusVariant: 'info',
      eta: '2025-11-28',
      progress: 0,
      kpis: [
        { k: 'Stations', v: '15' },
        { k: 'Duration', v: '3 days' },
      ],
    },
  ];

  const getStatusClass = (variant) => {
    const classes = {
      good: 'border-emerald-400/30 text-emerald-300 bg-emerald-500/10',
      info: 'border-sky-400/30 text-sky-300 bg-sky-500/10',
      warn: 'border-amber-400/30 text-amber-300 bg-amber-500/10',
    };
    return classes[variant] || classes.info;
  };

  return (
    <Card className="col-span-12 lg:col-span-6 animate-fade-in" data-testid="mission-summary">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Compass className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Mission Summary</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {missions.map((mission, idx) => (
          <div 
            key={idx} 
            className="p-4 rounded-md bg-secondary/40 border border-border/40"
            data-testid={`mission-${idx}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-medium text-foreground mb-1" data-testid={`mission-name-${idx}`}>{mission.name}</h4>
                <p className="text-xs text-muted-foreground">ETA: {mission.eta}</p>
              </div>
              <Badge 
                variant="outline" 
                className={getStatusClass(mission.statusVariant)}
                data-testid={`mission-status-${idx}`}
              >
                {mission.status}
              </Badge>
            </div>
            
            <div className="mb-3">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-muted-foreground">Progress</span>
                <span 
                  className={`font-mono ${
                    mission.progress >= 75 ? 'text-emerald-400' : 
                    mission.progress >= 50 ? 'text-cyan-400' : 
                    mission.progress >= 25 ? 'text-amber-400' : 
                    'text-muted-foreground'
                  }`}
                  data-testid={`mission-progress-${idx}`}
                >
                  {mission.progress}% {mission.progress < 25 && '(Behind)'} {mission.progress >= 75 && '(On Track)'}
                </span>
              </div>
              <Progress value={mission.progress} className="h-1.5" />
            </div>

            <div className="grid grid-cols-3 gap-3">
              {mission.kpis.map((kpi, kIdx) => (
                <div key={kIdx} data-testid={`mission-kpi-${idx}-${kIdx}`}>
                  <p className="text-xs text-muted-foreground mb-0.5">{kpi.k}</p>
                  <p className="text-sm font-medium font-mono">{kpi.v}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default MissionSummary;
